import { Card } from "./ui/card";
import { Link, useLocation } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { fmt } from "../lib/format";
import { cn } from "../lib/utils";
import { chapterForRoute } from "../lib/manual";

function HelpLink() {
  const { pathname } = useLocation();
  const id = chapterForRoute(pathname.startsWith("/settings") ? "/settings" : pathname);
  if (!id) return null;
  return <Link to={`/help?topic=${id}`} className="no-print inline-flex align-middle ml-2 text-muted-foreground hover:text-primary transition-colors" aria-label="Help for this page" title="Help for this page" data-testid="page-help-link"><HelpCircle className="h-5 w-5" /></Link>;
}

export const PALETTE = ["#0D9488", "#6366F1", "#F59E0B", "#F43F5E", "#3B82F6", "#10B981", "#8B5CF6", "#EC4899", "#84CC16", "#06B6D4", "#F97316", "#64748B"];

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6 sm:mb-8 fade-up">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid={testid || "page-title"}>{title}<HelpLink /></h1>
        {subtitle && <p className="text-muted-foreground mt-1.5 text-sm sm:text-base max-w-2xl">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2 no-print">{children}</div>}
    </div>
  );
}

export function Section({ title, action, children, className, testid, subtitle }) {
  return (
    <Card className={cn("p-4 sm:p-6 shadow-sm", className)} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            {title && <h3 className="text-lg font-bold tracking-tight">{title}</h3>}
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </Card>
  );
}

export function Kpi({ label, value, hint, tone, testid, icon: Icon }) {
  const color = tone === "income" ? "text-income" : tone === "expense" ? "text-expense" : tone === "warning" ? "text-warning" : "";
  return (
    <Card className="p-4 sm:p-5 shadow-sm hover:-translate-y-0.5 transition-transform duration-200" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <p className={cn("num text-xl sm:text-2xl font-semibold mt-2", color)} data-testid={testid ? `${testid}-value` : undefined}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </Card>
  );
}

export function Money({ value, className, signed, testid }) {
  const n = Number(value) || 0;
  return (
    <span className={cn("num", signed && (n > 0 ? "text-income" : n < 0 ? "text-expense" : ""), className)} data-testid={testid}>
      {signed && n > 0 ? "+" : ""}
      {fmt(n)}
    </span>
  );
}

export function NativeSelect({ value, onChange, children, className, testid, ...rest }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn("h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring", className)}
      data-testid={testid}
      {...rest}
    >
      {children}
    </select>
  );
}

export function CategorySelect({ categories, value, onChange, className, testid, includeAll }) {
  const groups = [...new Set(categories.map((c) => c.group))];
  return (
    <NativeSelect value={value || ""} onChange={onChange} className={className} testid={testid}>
      {includeAll && <option value="all">All categories</option>}
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {categories.filter((c) => c.group === g).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </optgroup>
      ))}
    </NativeSelect>
  );
}

export function Empty({ icon: Icon, title, text, children, testid }) {
  return (
    <div className="text-center py-12 px-4" data-testid={testid}>
      {Icon && <Icon className="h-10 w-10 mx-auto text-muted-foreground/60" />}
      <p className="font-display font-bold text-lg mt-3">{title}</p>
      {text && <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{text}</p>}
      {children && <div className="mt-5 flex flex-wrap gap-2 justify-center">{children}</div>}
    </div>
  );
}

export function Banner({ tone = "info", icon: Icon, children, testid, action }) {
  const cls = tone === "warning" ? "bg-warning-soft border-[hsl(var(--warning)/0.4)]" : tone === "danger" ? "bg-expense-soft border-[hsl(var(--expense)/0.4)]" : "bg-accent border-primary/20";
  return (
    <div className={cn("rounded-xl border px-4 py-3 flex items-start gap-3 text-sm", cls)} data-testid={testid}>
      {Icon && <Icon className="h-5 w-5 shrink-0 mt-0.5" />}
      <div className="flex-1">{children}</div>
      {action}
    </div>
  );
}

export function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
      {label && <p className="font-semibold mb-1">{label}</p>}
      {payload.map((p) => (
        <p key={p.dataKey || p.name} className="flex gap-2 items-center">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          {p.name}: <span className="num">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
}
